import { useAsync } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import type { Challenge, Me } from "@/lib/api";
import { studentApi } from "@/lib/api";

export interface Enrolment {
  apiKey: string | null;
  me: Me | null;
  /**
   * Whether the signed-in student belongs to the course that owns this challenge.
   *
   * `null` while unknown: signed out, or `/me` still in flight. Callers that gate a
   * student-only request on this should treat `null` as "not yet", not as "no".
   */
  enrolled: boolean | null;
}

/**
 * Who is looking at this challenge, and whether it is theirs to submit to.
 *
 * A student can browse any `public` challenge, including archived ones from earlier
 * terms and other courses, and the page should show them the leaderboard. What it should
 * not do is ask the server for *their* submissions, quota, or sessions there: the server
 * answers 403 for a challenge outside the caller's course, and the page was rendering
 * that as an error in a tab where the truthful answer is "you were never in this one".
 *
 * `/me` is the source of truth for the caller's course. The alternative -- attempting the
 * request and treating 403 as "not enrolled" -- would hide the real 403s, the ones a
 * disabled key or a retired term produce.
 */
export function useEnrolment(challenge: Challenge): Enrolment {
  const apiKey = useApiKey();
  const me = useAsync(apiKey ? () => studentApi.me(apiKey) : null, [apiKey]);
  const enrolled = me.data ? me.data.course.id === challenge.course_id : null;
  return { apiKey, me: me.data, enrolled };
}
